import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, CardBody, CardHeader, CardTitle, Col, Input, Row, Spinner } from "reactstrap";
import { toast } from "react-toastify";

import {
  buildCapabilitiesRequestBodyWithVpnTunnel,
  buildChunkedCapabilitiesRequestBodies,
  buildGatherCamerasRequestBodyWithVpnTunnel,
  buildRebootRequestBodyWithVpnTunnel,
  buildSetBitrateControlRequestBodyWithVpnTunnel,
  buildSetBitrateRequestBodyWithVpnTunnel,
  buildSetDhcpRequestBodyWithVpnTunnel,
  buildSetEncodingRequestBodyWithVpnTunnel,
  buildSetFpsRequestBodyWithVpnTunnel,
  buildSetResolutionRequestBodyWithVpnTunnel,
  channelPlaceholderRowsFromStore,
  channelRowsFromCapabilitiesResponse,
  channelRowsFromGatherResponse,
  formatDashcamOutcomeMessage,
  isDashcamOutcomeFailure,
  isDashcamRequestCancelled,
  mapDashcamHttpError,
  mergeChannelNamesFromPlaceholders,
  mergeChannelRowsFromCapabilitiesPatch,
  parseBitrateControlMode,
  parseBitrateKbps,
  parseDhcpMode,
  parseEncoding,
  parseFps,
  parseResolutionString,
  postCameras,
  postCapabilities,
  postCapabilitiesChunks,
  postReboot,
  postSetBitrate,
  postSetBitrateControl,
  postSetDhcp,
  postSetEncoding,
  postSetFps,
  postSetResolution,
  slotNumbersOfCapabilitiesPayload,
  streamTypeFromVideoRecorderSubType,
} from "../../services/capabilitiesService";
import { ChannelStreamCard, streamCardClassName } from "./ChannelStreamCard";
import BulkConfigPanel from "./BulkConfigPanel";
import captureDeviceToVideoRecorderShape from "./captureDeviceToVideoRecorderShape";
import "./CameraModelDvrPanel.scss";

const TITLE_NEED_STORE_ID_FOR_CURRENT_CONFIG = "A store ID is required to fetch the current config from Dashcam.";

// Gather uses "dynamic"; /set-dhcp response uses "dhcp" — handle both for display.
const DHCP_MODE_LABELS = { dynamic: "Dynamic (DHCP)", dhcp: "Dynamic (DHCP)", static: "Static" };

// The API reports brand_confidence as high/medium/low; render it in plainer terms for users.
const BRAND_CONFIDENCE_LABELS = { high: "very likely", medium: "likely", low: "unlikely" };

function formatDetectedBrand(brand, confidence) {
  if (!brand) return null;
  const label = String(brand);
  const conf = confidence ? BRAND_CONFIDENCE_LABELS[String(confidence).toLowerCase()] : null;
  return conf ? `${label} (${conf})` : label;
}

// Apply order matters: changing bitrate_control (CBR/VBR) on some firmware
// re-clamps the bitrate range, so commit it before resolution / fps / bitrate
// to reduce post-write divergence and partial-failure churn.
const FIELD_ORDER = ["bitrate_control", "resolution", "fps", "bitrate", "encoding"];

function streamKey(channel, streamType) {
  return `${channel}-${streamType}`;
}

/**
 * A camera's DVR channel number, or null when it has none. `Number(null)` is 0, which would
 * silently claim channel 0, so the null check has to come first.
 */
function cameraSlotNumber(camera) {
  if (camera?.slot_number == null) return null;
  const slot = Number(camera.slot_number);
  return Number.isFinite(slot) ? slot : null;
}

/** Join a camera's view names for its group label (parity with legacy channel_name). */
function viewNames(camera) {
  return (camera.views || [])
    .map((v) => v.view_name)
    .filter(Boolean)
    .join(", ");
}
/** A camera carries an analysis polygon when any view has a non-empty view_area. */
function cameraHasPolygon(camera) {
  return (camera.views || []).some((v) => Array.isArray(v.view_area) && v.view_area.length > 0);
}

function deviceValuesFromStream(s) {
  if (!s) return null;
  const resolution = s.width != null && s.height != null ? `${s.width}x${s.height}` : "";
  return {
    resolution,
    fps: s.fps != null ? String(s.fps) : "",
    bitrate: s.bitrate != null ? String(s.bitrate) : "",
    bitrate_control: s.bitrate_control ? String(s.bitrate_control).toLowerCase() : "",
    encoding: s.video_encoding ?? "",
  };
}

function seedDraftsFromGather(gatherRows) {
  const drafts = {};
  if (!Array.isArray(gatherRows)) return drafts;
  gatherRows.forEach((row) => {
    if (row.main) drafts[streamKey(row.channel, "main")] = deviceValuesFromStream(row.main);
    if (row.sub) drafts[streamKey(row.channel, "sub")] = deviceValuesFromStream(row.sub);
  });
  return drafts;
}

function dirtyFieldsBetween(draft, device) {
  const out = new Set();
  if (!draft || !device) return out;
  FIELD_ORDER.forEach((f) => {
    if ((draft[f] ?? "") !== (device[f] ?? "")) out.add(f);
  });
  return out;
}

function isDegradedReasonVisible(degradedReason) {
  return degradedReason != null && String(degradedReason).toLowerCase() !== "ok";
}

function ChannelStatusHeader({ row }) {
  const showDegraded = isDegradedReasonVisible(row.degraded_reason);
  const probeOutcome = row.probe_outcome != null && row.probe_outcome !== "" ? String(row.probe_outcome) : null;
  const hints = Array.isArray(row.hints) ? row.hints.filter((h) => h != null && String(h).trim() !== "") : [];
  if (!showDegraded && !probeOutcome && hints.length === 0) return null;

  return (
    <Row className="mb-2">
      <Col md="12">
        {showDegraded ? (
          <p className="text-center text-warning small mb-0">Status: {String(row.degraded_reason)}</p>
        ) : null}
        {probeOutcome ? <p className="text-center text-muted small mb-0">Probe: {probeOutcome}</p> : null}
        {hints.length > 0 ? (
          <ul className="list-unstyled text-center text-muted small mb-0">
            {hints.map((hint) => (
              <li key={hint}>{String(hint)}</li>
            ))}
          </ul>
        ) : null}
      </Col>
    </Row>
  );
}

function DegradedStreamPlaceholder({ channelNumber, channelName, streamType, degradedReason, isConfiguredStream }) {
  const namePart = channelName ? ` — ${channelName}` : "";
  return (
    <Card className={streamCardClassName({ isConfiguredStream })}>
      <CardHeader>
        <CardTitle className="text-center" tag="h5">
          Channel {channelNumber}
          {namePart} ({streamType})
        </CardTitle>
      </CardHeader>
      <CardBody className="d-flex flex-column flex-grow-1 justify-content-center">
        <p className="small text-warning text-center mb-0">Capabilities unavailable: {String(degradedReason)}</p>
      </CardBody>
    </Card>
  );
}

function CameraModelDvrPanel({
  device,
  brandKindById = {},
  hostnameByMachineId = {},
  localInference = false,
  storeId,
}) {
  // Reshape the CaptureDevice into the videoRecorder shape the engine + capabilitiesService
  // consume. channel_number is mapped from slot_number, so the entire ported engine below
  // runs unchanged. Memoized so the many effects keyed on `videoRecorder` are stable.
  const videoRecorder = useMemo(
    () => captureDeviceToVideoRecorderShape(device, { brandKindById, hostnameByMachineId, localInference }),
    [device, brandKindById, hostnameByMachineId, localInference]
  );
  const [loading, setLoading] = useState(false);
  const [camerasLoading, setCamerasLoading] = useState(false);
  const [singleFetchKey, setSingleFetchKey] = useState(null);
  const [rawOutput, setRawOutput] = useState("");
  const [channelRows, setChannelRows] = useState(null);
  const [gatherRows, setGatherRows] = useState(null);
  const [displayMeta, setDisplayMeta] = useState(null);

  // Per-stream draft state (lifted from ChannelStreamCard).
  const [drafts, setDrafts] = useState({});
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  // applyResults[key] = { loading, perField: { [field]: { verified?, applied?, error? } }, batchError? }
  const [applyResults, setApplyResults] = useState({});

  const hasStoreIdForCameras = Boolean(
    (storeId != null && String(storeId).trim() !== "") ||
      (videoRecorder?.store != null && String(videoRecorder.store).trim() !== "")
  );

  const vmChannelNumbers = useMemo(() => {
    if (videoRecorder?.video_recorder_type_name === "IP") return null;
    const channels = videoRecorder?.channels;
    if (!Array.isArray(channels) || channels.length === 0) return null;
    const s = new Set(channels.map((c) => Number(c.channel_number)).filter((n) => Number.isFinite(n)));
    return s.size > 0 ? s : null;
  }, [videoRecorder]);

  const placeholderRows = useMemo(() => channelPlaceholderRowsFromStore(videoRecorder), [videoRecorder]);
  const gatherDisplayRows = useMemo(() => {
    if (!Array.isArray(gatherRows) || gatherRows.length === 0) return null;
    const skeleton = gatherRows.map((r) => ({
      channel: r.channel,
      channel_name: null,
      main: null,
      sub: null,
      degraded_reason: null,
      probe_outcome: null,
      hints: [],
    }));
    return placeholderRows?.length ? mergeChannelNamesFromPlaceholders(skeleton, placeholderRows) : skeleton;
  }, [gatherRows, placeholderRows]);
  const displayRows = channelRows ?? gatherDisplayRows ?? placeholderRows;
  const headerMeta =
    displayMeta || (displayRows?.length ? { device_host: videoRecorder.device_ip, detected_brand: null } : null);

  const gatherLoaded = gatherRows !== null;
  const isIpCamera = videoRecorder?.video_recorder_type_name === "IP";

  // Camera-centric rendering keys every card off a Camera row, so a channel the device
  // reports that Manager has not modelled as a Camera has nowhere to render. Legacy showed
  // it (it iterated the device's own channels); silently dropping it would leave the
  // operator with a blank section and no explanation, so name those channels explicitly.
  // The mirror case — a camera whose slot the device did not answer for — keeps its own
  // section and says so per camera, since dropping it would make a camera vanish from a page
  // that was showing it a moment earlier (gather skips `not_found` slots, and the caps
  // response is filtered by the channels the device reported).
  const unmodelledChannels = useMemo(() => {
    if (!displayRows?.length) return [];
    const slots = new Set(
      (device?.cameras || []).map((camera) => cameraSlotNumber(camera)).filter((slot) => slot != null)
    );
    return displayRows.map((row) => row.channel).filter((channel) => !slots.has(Number(channel)));
  }, [displayRows, device]);
  const capsLoaded = Array.isArray(channelRows) && channelRows.length > 0;
  const phase = capsLoaded ? "CapsLoaded" : gatherLoaded ? "ConfigLoaded" : "Initial";

  const gatherRowByChannel = useMemo(() => {
    if (!gatherRows?.length) return new Map();
    return new Map(gatherRows.map((r) => [r.channel, r]));
  }, [gatherRows]);

  // DHCP is device-scoped — read the first non-empty value across all gathered streams.
  const deviceDhcp = useMemo(() => {
    if (!Array.isArray(gatherRows)) return "";
    const row = gatherRows.find((r) => r.main?.dhcp || r.sub?.dhcp);
    if (!row) return "";
    return String(row.main?.dhcp || row.sub?.dhcp).toLowerCase();
  }, [gatherRows]);

  useEffect(() => {
    setDhcpDraft(deviceDhcp);
  }, [deviceDhcp]);

  const deviceValuesByKey = useMemo(() => {
    const m = {};
    if (!gatherRows?.length) return m;
    gatherRows.forEach((row) => {
      if (row.main) m[streamKey(row.channel, "main")] = deviceValuesFromStream(row.main);
      if (row.sub) m[streamKey(row.channel, "sub")] = deviceValuesFromStream(row.sub);
    });
    return m;
  }, [gatherRows]);

  const dirtyByKey = useMemo(() => {
    const m = {};
    Object.keys(drafts).forEach((key) => {
      const deviceValues = deviceValuesByKey[key];
      m[key] = deviceValues
        ? dirtyFieldsBetween(drafts[key], deviceValues)
        : new Set(FIELD_ORDER.filter((f) => (drafts[key]?.[f] ?? "") !== ""));
    });
    return m;
  }, [drafts, deviceValuesByKey]);

  const dirtyStreamCount = useMemo(() => Object.values(dirtyByKey).filter((s) => s.size > 0).length, [dirtyByKey]);

  // Per-stream draft validation — Apply is blocked when a field is invalid (out of range,
  // non-integer bitrate). Surfaces inline so the user sees the error before clicking Apply.
  const invalidByKey = useMemo(() => {
    const m = {};
    if (!Array.isArray(channelRows)) return m;
    Object.keys(drafts).forEach((key) => {
      const draft = drafts[key];
      if (!draft) return;
      const [chStr, st] = key.split("-");
      const channel = Number(chStr);
      const row = channelRows.find((r) => r.channel === channel);
      const stream = row ? (st === "main" ? row.main : row.sub) : null;
      if (!stream) return;
      const issues = {};
      const dirty = dirtyByKey[key];
      if (Number.isFinite(stream.min_bitrate) && Number.isFinite(stream.max_bitrate)) {
        if (draft.bitrate === "" && dirty?.has("bitrate")) {
          issues.bitrate = "Required.";
        } else if (draft.bitrate !== "") {
          const n = Number(draft.bitrate);
          if (!Number.isInteger(n)) {
            issues.bitrate = "Must be a whole number.";
          } else if (n < stream.min_bitrate || n > stream.max_bitrate) {
            issues.bitrate = `Must be between ${stream.min_bitrate} and ${stream.max_bitrate} kbps.`;
          }
        }
      }
      if (Object.keys(issues).length > 0) m[key] = issues;
    });
    return m;
  }, [drafts, channelRows, dirtyByKey]);

  const [showBulkConfig, setShowBulkConfig] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkApplyReport, setBulkApplyReport] = useState(null);

  // Device-level DHCP state (not per-stream — /set-dhcp is device-scoped).
  const [dhcpDraft, setDhcpDraft] = useState("");
  const [dhcpApplying, setDhcpApplying] = useState(false);
  const [dhcpApplyResult, setDhcpApplyResult] = useState(null);

  // Reboot state
  const [rebootConfirming, setRebootConfirming] = useState(false);
  const [rebootLoading, setRebootLoading] = useState(false);
  const [rebootResult, setRebootResult] = useState(null);

  const batchAbortRef = useRef(null);
  const singleAbortRef = useRef(null);
  const camerasAbortRef = useRef(null);
  const streamApplyAbortsRef = useRef(new Map());
  const bulkAbortRef = useRef(null);
  const dhcpAbortRef = useRef(null);
  const rebootAbortRef = useRef(null);
  // Monotonic counter used to drop refetch responses from earlier Applies that arrive after a later
  // Apply's refetch — without this, out-of-order /cameras responses can clobber gatherRows.
  const refetchEpochRef = useRef(0);
  // Tracks which (key, field) pairs the user has explicitly edited. Used by the rebuild-drafts
  // effect to distinguish intentional edits from drafts that merely mirror old device values, so
  // a "Refresh config" that picks up an externally-changed device value reseeds untouched fields
  // instead of leaving them phantom-dirty.
  const userEditedRef = useRef(new Map());
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const abortActiveBatch = useCallback(() => {
    if (batchAbortRef.current) {
      batchAbortRef.current.abort();
      batchAbortRef.current = null;
    }
  }, []);

  const abortActiveSingle = useCallback(() => {
    if (singleAbortRef.current) {
      singleAbortRef.current.abort();
      singleAbortRef.current = null;
    }
  }, []);

  const abortActiveCameras = useCallback(() => {
    if (camerasAbortRef.current) {
      camerasAbortRef.current.abort();
      camerasAbortRef.current = null;
    }
  }, []);

  const abortAllStreamApplies = useCallback(() => {
    streamApplyAbortsRef.current.forEach((ctrl) => ctrl.abort());
    streamApplyAbortsRef.current.clear();
  }, []);

  const abortActiveBulk = useCallback(() => {
    if (bulkAbortRef.current) {
      bulkAbortRef.current.abort();
      bulkAbortRef.current = null;
    }
  }, []);

  const abortActiveDhcp = useCallback(() => {
    if (dhcpAbortRef.current) {
      dhcpAbortRef.current.abort();
      dhcpAbortRef.current = null;
    }
  }, []);

  const abortActiveReboot = useCallback(() => {
    if (rebootAbortRef.current) {
      rebootAbortRef.current.abort();
      rebootAbortRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      abortActiveBatch();
      abortActiveSingle();
      abortActiveCameras();
      abortAllStreamApplies();
      abortActiveBulk();
      abortActiveDhcp();
      abortActiveReboot();
    };
  }, [
    abortActiveBatch,
    abortActiveSingle,
    abortActiveCameras,
    abortAllStreamApplies,
    abortActiveBulk,
    abortActiveDhcp,
    abortActiveReboot,
  ]);

  const anyApplyInFlight = Object.values(applyResults).some((r) => r?.loading);

  // Rebuild drafts whenever gather changes. For each stream, fields the user has explicitly
  // edited (tracked in userEditedRef) keep their previous draft value; untouched fields reseed
  // from the new device values. This avoids phantom-dirty state when "Refresh config" picks up
  // an externally-changed device value the user never edited.
  useEffect(() => {
    if (!gatherRows) {
      setDrafts({});
      userEditedRef.current.clear();
      return;
    }
    setDrafts((prev) => {
      const seeded = seedDraftsFromGather(gatherRows);
      const next = {};
      Object.keys(seeded).forEach((key) => {
        const prevDraft = prev[key];
        const edited = userEditedRef.current.get(key);
        if (!prevDraft || !edited || edited.size === 0) {
          next[key] = seeded[key];
          return;
        }
        const merged = { ...seeded[key] };
        edited.forEach((f) => {
          if (prevDraft[f] !== undefined) merged[f] = prevDraft[f];
        });
        next[key] = merged;
      });
      Array.from(userEditedRef.current.keys()).forEach((k) => {
        if (!(k in seeded)) userEditedRef.current.delete(k);
      });
      return next;
    });
  }, [gatherRows]);

  // When capabilities load without current config, seed empty drafts so editors are visible.
  useEffect(() => {
    if (!channelRows?.length) return;
    setDrafts((prev) => {
      const next = { ...prev };
      let changed = false;
      channelRows.forEach((row) => {
        ["main", "sub"].forEach((st) => {
          const stream = st === "main" ? row.main : row.sub;
          if (!stream) return;
          const key = streamKey(row.channel, st);
          if (!next[key]) {
            next[key] = {
              resolution: "",
              fps: "",
              bitrate: "",
              bitrate_control: "",
              encoding: "",
            };
            changed = true;
          }
        });
      });
      return changed ? next : prev;
    });
  }, [channelRows]);

  // When capabilities load or refresh, snap back any draft value that fell outside the new
  // supported list. Without this the dropdown would render with no matching option and Apply
  // would POST a value the device may reject.
  useEffect(() => {
    if (!capsLoaded || !gatherRows) return;
    const currentDrafts = draftsRef.current;
    const nextDrafts = { ...currentDrafts };
    let changed = false;
    Object.keys(currentDrafts).forEach((key) => {
      const draft = currentDrafts[key];
      const deviceValues = deviceValuesByKey[key];
      if (!draft || !deviceValues) return;
      const [chStr, st] = key.split("-");
      const channel = Number(chStr);
      const row = channelRows.find((r) => r.channel === channel);
      const stream = row ? (st === "main" ? row.main : row.sub) : null;
      if (!stream) return;
      const supportedRes = Array.isArray(stream.supported_resolutions) ? stream.supported_resolutions : [];
      const supportedFps = Array.isArray(stream.supported_fps) ? stream.supported_fps.map(String) : [];
      const supportedModes = Array.isArray(stream.supported_bitrate_control_modes)
        ? stream.supported_bitrate_control_modes.map((m) => String(m).toLowerCase())
        : [];
      const updated = { ...draft };
      let kChanged = false;
      if (
        draft.resolution &&
        draft.resolution !== deviceValues.resolution &&
        supportedRes.length > 0 &&
        !supportedRes.includes(draft.resolution)
      ) {
        updated.resolution = deviceValues.resolution;
        kChanged = true;
      }
      if (draft.fps && draft.fps !== deviceValues.fps && supportedFps.length > 0 && !supportedFps.includes(draft.fps)) {
        updated.fps = deviceValues.fps;
        kChanged = true;
      }
      if (
        draft.bitrate_control &&
        draft.bitrate_control !== deviceValues.bitrate_control &&
        supportedModes.length > 0 &&
        !supportedModes.includes(draft.bitrate_control)
      ) {
        updated.bitrate_control = deviceValues.bitrate_control;
        kChanged = true;
      }
      if (
        Number.isFinite(stream.min_bitrate) &&
        Number.isFinite(stream.max_bitrate) &&
        draft.bitrate &&
        draft.bitrate !== deviceValues.bitrate
      ) {
        const n = Number(draft.bitrate);
        if (Number.isFinite(n) && (n < stream.min_bitrate || n > stream.max_bitrate)) {
          updated.bitrate = deviceValues.bitrate;
          kChanged = true;
        }
      }
      if (kChanged) {
        nextDrafts[key] = updated;
        changed = true;
      }
    });
    if (changed) {
      setDrafts(nextDrafts);
      toast.info("Some drafts cleared — values no longer supported by capabilities.");
    }
    // Intentionally exclude gatherRows / deviceValuesByKey from deps: this effect only needs to
    // run when capabilities change, not on every "Refresh config". The body still reads the
    // latest device values via closure on each render that triggers a caps change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelRows, capsLoaded]);

  const handleGetAll = async () => {
    if (anyApplyInFlight) return;
    abortActiveCameras();
    abortActiveSingle();
    abortActiveBatch();
    const controller = new AbortController();
    batchAbortRef.current = controller;
    const { signal } = controller;

    setLoading(true);
    setRawOutput("");
    setChannelRows(null);
    setDisplayMeta(null);

    try {
      // One request per group of channels rather than a whole-device sweep: a sweep of a large DVR
      // outlives the proxy's read timeout and comes back 504. Rows are merged as each group lands,
      // so the grid fills progressively and a failed group costs only its own channels.
      const payloads = await buildChunkedCapabilitiesRequestBodies(videoRecorder, { localInference }, { signal });
      const placeholders = channelPlaceholderRowsFromStore(videoRecorder);
      const rowByChannel = new Map();
      const failures = [];
      let meta = null;

      await postCapabilitiesChunks(payloads, {
        signal,
        onResult: ({ payload, data, error }) => {
          if (error || isDashcamOutcomeFailure(data)) {
            failures.push({ payload, data, error });
            return;
          }
          channelRowsFromCapabilitiesResponse(data, vmChannelNumbers).forEach((row) => {
            rowByChannel.set(row.channel, row);
          });
          if (meta == null) {
            meta = {
              device_host: data.device_host,
              detected_brand: data.detected_brand,
              brand_confidence: data.brand_confidence,
            };
          }
          const grouped = [...rowByChannel.values()].sort((a, b) => a.channel - b.channel);
          setChannelRows(mergeChannelNamesFromPlaceholders(grouped, placeholders));
          setDisplayMeta(meta);
          setRawOutput("");
        },
      });

      if (signal.aborted) return;

      if (rowByChannel.size === 0) {
        const first = failures[0];
        const message = first?.error ? mapDashcamHttpError(first.error) : formatDashcamOutcomeMessage(first?.data);
        const body = first?.data ?? first?.error?.response?.data ?? { message };
        setChannelRows(null);
        setDisplayMeta(null);
        setRawOutput(JSON.stringify(body, null, 2));
        toast.error(`Get DVR capabilities failed: ${message}`);
        return;
      }
      if (failures.length > 0) {
        const slots = failures.flatMap((f) => slotNumbersOfCapabilitiesPayload(f.payload) ?? []);
        toast.warning(
          slots.length
            ? `Capabilities incomplete — channels ${slots.join(", ")} failed.`
            : "Capabilities incomplete — some channels failed."
        );
      }
    } catch (err) {
      if (signal.aborted || isDashcamRequestCancelled(err)) return;
      const fallback = { message: mapDashcamHttpError(err) };
      const body = err.response?.data !== undefined ? err.response.data : fallback;
      if (!signal.aborted) {
        setChannelRows(null);
        setDisplayMeta(null);
        setRawOutput(JSON.stringify(body, null, 2));
      }
    } finally {
      if (batchAbortRef.current === controller) {
        batchAbortRef.current = null;
      }
      if (!signal.aborted || batchAbortRef.current == null) {
        setLoading(false);
      }
    }
  };

  const handleGetChannelStream = async (channel, streamType) => {
    if (anyApplyInFlight) return;
    const baseRows = channelRows ?? placeholderRows;
    if (!baseRows?.length) return;

    abortActiveCameras();
    abortActiveBatch();
    abortActiveSingle();
    const controller = new AbortController();
    singleAbortRef.current = controller;
    const { signal } = controller;
    const fetchKey = `${channel}-${streamType}`;

    setSingleFetchKey(fetchKey);
    setRawOutput("");

    try {
      const payload = await buildCapabilitiesRequestBodyWithVpnTunnel(
        videoRecorder,
        { localInference, channel, streamType },
        { signal }
      );
      const { data } = await postCapabilities(payload, { signal });
      if (!signal.aborted) {
        if (isDashcamOutcomeFailure(data)) {
          const outcomeError = formatDashcamOutcomeMessage(data);
          setRawOutput(JSON.stringify(data, null, 2));
          toast.error(`Get channel ${channel}/${streamType} capabilities failed: ${outcomeError}`);
          return;
        }
        setChannelRows((prev) => {
          const base = prev ?? placeholderRows;
          if (!base?.length) return prev;
          return mergeChannelRowsFromCapabilitiesPatch(base, data, channel, streamType);
        });
        setDisplayMeta((prev) => ({
          device_host: data.device_host ?? prev?.device_host,
          detected_brand: data.detected_brand ?? prev?.detected_brand,
          brand_confidence: data.brand_confidence ?? prev?.brand_confidence,
        }));
      }
    } catch (err) {
      if (signal.aborted || isDashcamRequestCancelled(err)) return;
      const fallback = { message: mapDashcamHttpError(err) };
      const body = err.response?.data !== undefined ? err.response.data : fallback;
      if (!signal.aborted) {
        setRawOutput(JSON.stringify(body, null, 2));
      }
    } finally {
      if (singleAbortRef.current === controller) {
        singleAbortRef.current = null;
      }
      if (!signal.aborted || singleAbortRef.current == null) {
        setSingleFetchKey(null);
      }
    }
  };

  const fetchCurrentConfig = useCallback(
    async (signal) => {
      const payload = await buildGatherCamerasRequestBodyWithVpnTunnel(
        videoRecorder,
        storeId,
        { localInference },
        { signal }
      );
      if (!payload.store_id) {
        throw new Error("Store id is required to fetch current config from Dashcam.");
      }
      const { data } = await postCameras(payload, { signal });
      const outcomeError = formatDashcamOutcomeMessage(data);
      if (outcomeError) {
        throw new Error(outcomeError);
      }
      const grouped = channelRowsFromGatherResponse(data);
      const placeholders = channelPlaceholderRowsFromStore(videoRecorder);
      let next;
      if (grouped.length > 0 && placeholders?.length) {
        next = mergeChannelNamesFromPlaceholders(grouped, placeholders);
      } else if (grouped.length > 0) {
        next = grouped;
      } else {
        next = [];
      }
      const meta = {
        device_host: data.device_host,
        detected_brand: data.detected_brand,
        brand_confidence: data.brand_confidence,
      };
      return { rows: next, meta };
    },
    [videoRecorder, storeId, localInference]
  );

  const handleGetCurrentConfig = async () => {
    if (!hasStoreIdForCameras) {
      setRawOutput(JSON.stringify({ message: "Store id is required to fetch current config from Dashcam." }, null, 2));
      return;
    }
    if (anyApplyInFlight) return;

    abortActiveBatch();
    abortActiveSingle();
    abortActiveCameras();
    abortAllStreamApplies();
    const controller = new AbortController();
    camerasAbortRef.current = controller;
    const { signal } = controller;

    setCamerasLoading(true);
    setRawOutput("");

    try {
      const { rows, meta } = await fetchCurrentConfig(signal);
      // Note: this setGatherRows is not epoch-protected. Safe only because abortAllStreamApplies()
      // ran above, so any in-flight applyStream refetch sees signal.aborted and skips its own
      // setGatherRows. Don't add a non-aborting setGatherRows path without bumping refetchEpochRef.
      if (!signal.aborted) {
        setGatherRows(rows);
        setDisplayMeta(meta);
        setRawOutput("");
        setApplyResults({});
        setDhcpApplyResult(null);
      }
    } catch (err) {
      if (signal.aborted || isDashcamRequestCancelled(err)) return;
      if (!signal.aborted) {
        toast.error(`Failed to fetch current config: ${mapDashcamHttpError(err)}`);
      }
    } finally {
      if (camerasAbortRef.current === controller) {
        camerasAbortRef.current = null;
      }
      if (!signal.aborted || camerasAbortRef.current == null) {
        setCamerasLoading(false);
      }
    }
  };

  const setDraftField = useCallback((key, field, value) => {
    let edited = userEditedRef.current.get(key);
    if (!edited) {
      edited = new Set();
      userEditedRef.current.set(key, edited);
    }
    edited.add(field);
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? {}), [field]: value },
    }));
  }, []);

  const resetStream = useCallback(
    (key) => {
      const deviceValues = deviceValuesByKey[key];
      if (!deviceValues) return;
      userEditedRef.current.delete(key);
      setDrafts((prev) => ({ ...prev, [key]: { ...deviceValues } }));
      setApplyResults((prev) => {
        if (!prev[key]) return prev;
        const { [key]: _, ...rest } = prev;
        return rest;
      });
    },
    [deviceValuesByKey]
  );

  const handleApplyDhcp = useCallback(async () => {
    const mode = parseDhcpMode(dhcpDraft);
    if (mode == null) return;
    abortActiveDhcp();
    const controller = new AbortController();
    dhcpAbortRef.current = controller;
    const { signal } = controller;
    setDhcpApplying(true);
    setDhcpApplyResult(null);
    try {
      const payload = await buildSetDhcpRequestBodyWithVpnTunnel(
        videoRecorder,
        { localInference, mode, storeId },
        { signal }
      );
      const { data } = await postSetDhcp(payload, { signal });
      const outcomeError = formatDashcamOutcomeMessage(data);
      if (outcomeError) throw new Error(outcomeError);
      // The mutation landed on the device — surface the result even if the user
      // navigated away while the request was in flight (signal may be aborted).
      if (mountedRef.current) {
        setDhcpApplyResult({ submitted: true, verified: data?.verified, applied: data?.applied_mode });
      }
    } catch (err) {
      if (signal.aborted || isDashcamRequestCancelled(err)) return;
      if (mountedRef.current) {
        setDhcpApplyResult({ error: mapDashcamHttpError(err) });
      }
    } finally {
      if (dhcpAbortRef.current === controller) dhcpAbortRef.current = null;
      if (mountedRef.current) setDhcpApplying(false);
    }
  }, [dhcpDraft, videoRecorder, localInference, storeId, abortActiveDhcp]);

  const buildAndPostFor = useCallback(
    async (field, channel, streamType, draft, signal) => {
      const postAndCheck = async (poster, payload) => {
        const { data } = await poster(payload, { signal });
        const outcomeError = formatDashcamOutcomeMessage(data);
        if (outcomeError) throw new Error(outcomeError);
        return data;
      };
      switch (field) {
        case "bitrate_control": {
          const mode = parseBitrateControlMode(draft.bitrate_control);
          if (mode == null) throw new Error("Invalid mode — must be cbr or vbr.");
          const payload = await buildSetBitrateControlRequestBodyWithVpnTunnel(
            videoRecorder,
            { localInference, channel, streamType, mode, storeId },
            { signal }
          );
          const data = await postAndCheck(postSetBitrateControl, payload);
          return { verified: data?.verified, applied: data?.applied_mode };
        }
        case "resolution": {
          const dims = parseResolutionString(draft.resolution);
          if (!dims) throw new Error("Invalid resolution.");
          const payload = await buildSetResolutionRequestBodyWithVpnTunnel(
            videoRecorder,
            { localInference, channel, streamType, width: dims.width, height: dims.height, storeId },
            { signal }
          );
          const data = await postAndCheck(postSetResolution, payload);
          return { verified: data?.verified, applied: { w: data?.applied_width, h: data?.applied_height } };
        }
        case "fps": {
          const fps = parseFps(draft.fps);
          if (fps == null) throw new Error("Invalid FPS — enter a positive whole number.");
          const payload = await buildSetFpsRequestBodyWithVpnTunnel(
            videoRecorder,
            { localInference, channel, streamType, fps, storeId },
            { signal }
          );
          const data = await postAndCheck(postSetFps, payload);
          return { verified: data?.verified, applied: data?.applied_fps };
        }
        case "bitrate": {
          const bitrate = parseBitrateKbps(draft.bitrate);
          if (bitrate == null) throw new Error("Invalid bitrate — enter a whole number.");
          const payload = await buildSetBitrateRequestBodyWithVpnTunnel(
            videoRecorder,
            { localInference, channel, streamType, bitrate, storeId },
            { signal }
          );
          const data = await postAndCheck(postSetBitrate, payload);
          return { verified: data?.verified, applied: data?.applied_bitrate };
        }
        case "encoding": {
          const codec = parseEncoding(draft.encoding);
          if (!codec) throw new Error("Invalid encoding — select a supported encoding.");
          const payload = await buildSetEncodingRequestBodyWithVpnTunnel(
            videoRecorder,
            { localInference, channel, streamType, codec, storeId },
            { signal }
          );
          const data = await postAndCheck(postSetEncoding, payload);
          return { verified: data?.verified, applied: data?.applied_codec };
        }
        default:
          throw new Error(`Unknown field: ${field}`);
      }
    },
    [videoRecorder, localInference, storeId]
  );

  const applyStream = useCallback(
    async (channel, streamType) => {
      const key = streamKey(channel, streamType);
      const dirty = dirtyByKey[key];
      if (!dirty || dirty.size === 0) return;

      const prevController = streamApplyAbortsRef.current.get(key);
      if (prevController) prevController.abort();
      const controller = new AbortController();
      streamApplyAbortsRef.current.set(key, controller);
      const { signal } = controller;

      // Clear stale per-field results for fields we are about to apply.
      setApplyResults((prev) => ({ ...prev, [key]: { loading: true, perField: {} } }));

      const draftSnapshot = drafts[key];
      const perField = {};
      let abortedDuringWrites = false;
      let batchError;

      try {
        await FIELD_ORDER.reduce(async (prevP, field) => {
          await prevP;
          if (abortedDuringWrites || !dirty.has(field)) return;
          try {
            const result = await buildAndPostFor(field, channel, streamType, draftSnapshot, signal);
            if (signal.aborted) {
              abortedDuringWrites = true;
              return;
            }
            perField[field] = { verified: result.verified, applied: result.applied };
            const editedSet = userEditedRef.current.get(key);
            if (editedSet) {
              editedSet.delete(field);
              if (editedSet.size === 0) userEditedRef.current.delete(key);
            }
          } catch (err) {
            if (signal.aborted || isDashcamRequestCancelled(err)) {
              abortedDuringWrites = true;
              return;
            }
            perField[field] = { error: mapDashcamHttpError(err) };
          }
        }, Promise.resolve());

        if (!abortedDuringWrites) {
          // Tag this refetch with a monotonic epoch; if a later Apply finishes its writes
          // while ours is still in flight, drop our stale refetch result.
          refetchEpochRef.current += 1;
          const epoch = refetchEpochRef.current;
          try {
            const { rows } = await fetchCurrentConfig(signal);
            if (!signal.aborted && epoch === refetchEpochRef.current) {
              setGatherRows(rows);
            }
          } catch (err) {
            if (!signal.aborted && !isDashcamRequestCancelled(err)) {
              batchError = mapDashcamHttpError(err);
            }
          }
        }
      } finally {
        if (streamApplyAbortsRef.current.get(key) === controller) {
          streamApplyAbortsRef.current.delete(key);
        }
        if (mountedRef.current) {
          if (signal.aborted) {
            // Aborted: drop the loading entry entirely so anyApplyInFlight clears.
            setApplyResults((prev) => {
              if (!prev[key]) return prev;
              const { [key]: _drop, ...rest } = prev;
              return rest;
            });
          } else {
            setApplyResults((prev) => ({
              ...prev,
              [key]: { loading: false, perField, ...(batchError ? { batchError } : {}) },
            }));
            const failedFields = Object.keys(perField).filter((f) => perField[f].error);
            if (failedFields.length > 0 || batchError) {
              const summary = batchError
                ? `Apply ${channel}/${streamType}: refresh after Apply failed (${batchError}).`
                : `Apply ${channel}/${streamType}: ${failedFields.join(", ")} failed.`;
              toast.error(summary);
            }
          }
        }
      }
    },
    [dirtyByKey, drafts, buildAndPostFor, fetchCurrentConfig]
  );

  const handleBulkApply = useCallback(
    async (selectedChannels, bulkStreamType, bulkDraft) => {
      if (anyApplyInFlight || bulkApplying) return;
      abortActiveBulk();
      const controller = new AbortController();
      bulkAbortRef.current = controller;
      const { signal } = controller;

      setBulkApplying(true);
      setBulkApplyReport(null);
      try {
        let cancelled = false;
        const channelResults = {};
        await selectedChannels.reduce(async (prevKeyP, channel) => {
          await prevKeyP;
          if (cancelled || signal.aborted) return;
          channelResults[channel] = { attempted: 0, succeeded: 0 };
          const streamType = bulkStreamType;
          await FIELD_ORDER.reduce(async (prevFieldP, field) => {
            await prevFieldP;
            if (cancelled || signal.aborted) return;
            const value = bulkDraft[field];
            if (value == null || value === "") return;
            channelResults[channel].attempted += 1;
            try {
              await buildAndPostFor(field, channel, streamType, bulkDraft, signal);
              channelResults[channel].succeeded += 1;
            } catch (err) {
              if (signal.aborted || isDashcamRequestCancelled(err)) {
                cancelled = true;
                return;
              }
              toast.error(`Ch${channel}/${streamType}/${field}: ${mapDashcamHttpError(err)}`);
            }
          }, Promise.resolve());
        }, Promise.resolve());

        if (!signal.aborted) {
          const complete = [];
          const partial = [];
          const failed = [];
          Object.entries(channelResults).forEach(([ch, { attempted, succeeded }]) => {
            if (attempted === 0) return;
            const num = Number(ch);
            if (succeeded === attempted) complete.push(num);
            else if (succeeded === 0) failed.push(num);
            else partial.push(num);
          });
          if (mountedRef.current) setBulkApplyReport({ complete, partial, failed });

          if (hasStoreIdForCameras) {
            refetchEpochRef.current += 1;
            const epoch = refetchEpochRef.current;
            try {
              const { rows } = await fetchCurrentConfig(signal);
              if (!signal.aborted && epoch === refetchEpochRef.current) {
                setGatherRows(rows);
              }
            } catch (err) {
              if (!signal.aborted && !isDashcamRequestCancelled(err)) {
                toast.error(`Bulk apply: config refresh failed (${mapDashcamHttpError(err)})`);
              }
            }
          }
        }
      } finally {
        if (bulkAbortRef.current === controller) bulkAbortRef.current = null;
        if (mountedRef.current) setBulkApplying(false);
      }
    },
    [anyApplyInFlight, bulkApplying, abortActiveBulk, buildAndPostFor, fetchCurrentConfig, hasStoreIdForCameras]
  );

  const handleReboot = useCallback(async () => {
    setRebootConfirming(false);
    abortActiveReboot();
    const controller = new AbortController();
    rebootAbortRef.current = controller;
    const { signal } = controller;
    setRebootLoading(true);
    setRebootResult(null);
    try {
      const payload = await buildRebootRequestBodyWithVpnTunnel(videoRecorder, { localInference, storeId }, { signal });
      const { data } = await postReboot(payload, { signal });
      const outcomeError = formatDashcamOutcomeMessage(data);
      if (outcomeError) throw new Error(outcomeError);
      if (mountedRef.current) {
        setRebootResult({ success: true });
        toast.success("DVR reboot initiated.");
      }
    } catch (err) {
      if (signal.aborted || isDashcamRequestCancelled(err)) return;
      const msg = mapDashcamHttpError(err);
      if (mountedRef.current) {
        setRebootResult({ error: msg });
        toast.error(`Reboot failed: ${msg}`);
      }
    } finally {
      if (rebootAbortRef.current === controller) rebootAbortRef.current = null;
      if (mountedRef.current) setRebootLoading(false);
    }
  }, [videoRecorder, localInference, storeId, abortActiveReboot]);

  const refreshDisabled =
    loading ||
    camerasLoading ||
    singleFetchKey != null ||
    anyApplyInFlight ||
    bulkApplying ||
    dhcpApplying ||
    rebootLoading;
  const capsButtonDisabled = refreshDisabled || !gatherLoaded;

  const dirtyBannerCopy =
    dirtyStreamCount === 1
      ? "1 stream with unsaved changes — Apply or Reset."
      : `${dirtyStreamCount} streams with unsaved changes — Apply or Reset.`;

  return (
    <Card className="video-recorder">
      <CardHeader>
        <CardTitle tag="h4" className="d-flex align-items-center flex-wrap">
          <span>{device.name || device.host || device.public_id}</span>
        </CardTitle>
      </CardHeader>
      <CardBody>
        <div className="mb-3 d-flex align-items-center flex-wrap">
          <Button
            type="button"
            color="primary"
            disabled={refreshDisabled || !hasStoreIdForCameras}
            onClick={handleGetCurrentConfig}
            title={hasStoreIdForCameras ? undefined : TITLE_NEED_STORE_ID_FOR_CURRENT_CONFIG}
          >
            {camerasLoading && <Spinner size="sm" className="mr-2" />}
            {phase === "Initial" ? "Get current config" : "Refresh config"}
          </Button>
          <Button color="primary" disabled={capsButtonDisabled} onClick={handleGetAll}>
            {loading && <Spinner size="sm" className="mr-2" />}
            {phase === "CapsLoaded" ? "Refresh capabilities" : "Get DVR capabilities"}
          </Button>
          {!isIpCamera && (
            <Button
              color={showBulkConfig ? "secondary" : "outline-secondary"}
              disabled={refreshDisabled}
              onClick={() => setShowBulkConfig((v) => !v)}
            >
              Bulk Config
              <i className={`fas ml-2 ${showBulkConfig ? "fa-angle-up" : "fa-angle-down"}`} />
            </Button>
          )}
          {!rebootConfirming ? (
            <Button
              color="danger"
              disabled={refreshDisabled}
              onClick={() => {
                setRebootConfirming(true);
                setRebootResult(null);
              }}
            >
              Reboot DVR
            </Button>
          ) : (
            <>
              <span className="text-danger small mr-2">Confirm reboot?</span>
              <Button color="danger" size="sm" disabled={rebootLoading} onClick={handleReboot}>
                {rebootLoading && <Spinner size="sm" className="mr-2" />}
                Yes, reboot
              </Button>
              <Button color="secondary" size="sm" disabled={rebootLoading} onClick={() => setRebootConfirming(false)}>
                Cancel
              </Button>
            </>
          )}
          {rebootResult?.success && <span className="small text-success">Reboot initiated.</span>}
          {rebootResult?.error && <span className="small text-danger">{rebootResult.error}</span>}
        </div>
        {!isIpCamera && showBulkConfig && (
          <BulkConfigPanel
            displayRows={displayRows}
            channelRows={channelRows}
            onApply={handleBulkApply}
            applying={bulkApplying}
            applyReport={bulkApplyReport}
          />
        )}
        {!isIpCamera && gatherLoaded && deviceDhcp && (
          <div className="mb-3 p-2 border rounded">
            <h6 className="text-muted text-uppercase small mb-2">Device Global Config</h6>
            <p className="mb-2">
              <strong>DHCP:</strong> <span>{DHCP_MODE_LABELS[deviceDhcp] ?? deviceDhcp}</span>
            </p>
            <div className="d-flex align-items-center flex-wrap" style={{ gap: "0.5rem" }}>
              <Input
                type="select"
                bsSize="sm"
                style={{ maxWidth: "13rem" }}
                value={dhcpDraft}
                onChange={(e) => {
                  setDhcpDraft(e.target.value);
                  setDhcpApplyResult(null);
                }}
                disabled={dhcpApplying || refreshDisabled}
              >
                {!dhcpDraft && <option value="" disabled hidden />}
                <option value="dynamic">{DHCP_MODE_LABELS.dynamic}</option>
                <option value="static">{DHCP_MODE_LABELS.static}</option>
              </Input>
              <Button
                color="success"
                size="sm"
                disabled={!dhcpDraft || dhcpDraft === deviceDhcp || dhcpApplying || refreshDisabled}
                onClick={handleApplyDhcp}
              >
                {dhcpApplying && <Spinner size="sm" className="mr-2" />}
                Apply DHCP
              </Button>
              {dhcpApplyResult?.submitted && !dhcpApplyResult.error && (
                <span className="small text-success">
                  Applied
                  {dhcpApplyResult.applied
                    ? ` ${DHCP_MODE_LABELS[dhcpApplyResult.applied] ?? dhcpApplyResult.applied}`
                    : ""}
                  {dhcpApplyResult.verified ? " (verified)" : ""}
                </span>
              )}
              {dhcpApplyResult?.error && <span className="small text-danger">{dhcpApplyResult.error}</span>}
            </div>
          </div>
        )}
        {!isIpCamera && dhcpApplyResult?.submitted && !dhcpApplyResult.error && (
          <Alert color="warning" className="mb-3">
            DVR network mode changed — device IP may have changed. Verify connectivity before refreshing config.
          </Alert>
        )}
        {phase === "CapsLoaded" && dirtyStreamCount > 0 ? (
          <Alert color="info" className="mb-3" role="status">
            {dirtyBannerCopy}
          </Alert>
        ) : null}
        {headerMeta && displayRows && displayRows.length > 0 ? (
          <div className="video-recorder-capabilities">
            <Row>
              <Col md="12" className="mb-3">
                <h4 className="text-center">DVR Capabilities</h4>
                {unmodelledChannels.length > 0 && (
                  <Alert color="warning" className="mt-2 mb-0">
                    {`Channel${unmodelledChannels.length > 1 ? "s" : ""} ${unmodelledChannels.join(
                      ", "
                    )} reported by the device ${
                      unmodelledChannels.length > 1 ? "have" : "has"
                    } no matching camera in Manager, so ${
                      unmodelledChannels.length > 1 ? "they are" : "it is"
                    } not configurable here. Add the camera to the store, or use the legacy interface.`}
                  </Alert>
                )}
                {(() => {
                  const brandText = formatDetectedBrand(headerMeta.detected_brand, headerMeta.brand_confidence);
                  if (!brandText && !headerMeta.device_host) return null;
                  return (
                    <p className="text-muted text-center mb-0">
                      {brandText && `Detected brand: ${brandText}`}
                      {brandText && headerMeta.device_host && " | "}
                      {headerMeta.device_host && `Device IP: ${headerMeta.device_host}`}
                    </p>
                  );
                })()}
                {isIpCamera && videoRecorder.rtsp_url_suffix && (
                  <p className="text-muted text-center mb-0">RTSP URL suffix: {videoRecorder.rtsp_url_suffix}</p>
                )}
              </Col>
            </Row>
            {[...(device.cameras || [])]
              .sort((a, b) => (a.slot_number ?? 0) - (b.slot_number ?? 0))
              .map((camera) => {
                const slot = cameraSlotNumber(camera);
                const row = slot == null ? null : displayRows.find((r) => r.channel === slot);
                const gatherRow = row ? gatherRowByChannel.get(row.channel) ?? null : null;
                // `stream_quality` is per-Camera in the camera model (one device can serve
                // slot 1 from SUB and slot 2 from MAIN), unlike the legacy VideoRecorder's
                // single device-wide `sub_type` — so resolve the highlight per camera. No
                // fallback to `sub_type`: it *is* cameras[0].stream_quality, so falling back
                // would highlight every unset camera with camera 1's stream.
                const configuredStreamType = streamTypeFromVideoRecorderSubType(camera.stream_quality);
                const renderCard = (streamType) => {
                  // --- verbatim copy of legacy renderCard body (1205–1253) ---
                  const stream = streamType === "main" ? row.main : row.sub;
                  const currentStream = streamType === "main" ? gatherRow?.main : gatherRow?.sub;
                  if (!stream && !currentStream) {
                    if (isDegradedReasonVisible(row.degraded_reason)) {
                      return (
                        <Col md="6" className="d-flex mb-2 mb-md-0" key={`${row.channel}-${streamType}-degraded`}>
                          <DegradedStreamPlaceholder
                            channelNumber={row.channel}
                            channelName={row.channel_name}
                            streamType={streamType}
                            degradedReason={row.degraded_reason}
                            isConfiguredStream={!isIpCamera && configuredStreamType === streamType}
                          />
                        </Col>
                      );
                    }
                    return null;
                  }
                  const key = streamKey(row.channel, streamType);
                  const draft = drafts[key];
                  const deviceValues = deviceValuesByKey[key];
                  const dirtyFields = dirtyByKey[key] ?? new Set();
                  const applyResult = applyResults[key];
                  const invalidFields = invalidByKey[key];
                  return (
                    <Col md="6" className="d-flex mb-2 mb-md-0" key={`${row.channel}-${streamType}`}>
                      <ChannelStreamCard
                        channelNumber={row.channel}
                        channelName={row.channel_name}
                        streamType={streamType}
                        stream={stream}
                        currentStream={currentStream ?? null}
                        streamLoading={singleFetchKey === key}
                        dashcamBusy={loading || camerasLoading || bulkApplying}
                        gatherLoaded={gatherLoaded}
                        onGetCapabilities={() => handleGetChannelStream(row.channel, streamType)}
                        draft={draft}
                        deviceValues={deviceValues}
                        dirtyFields={dirtyFields}
                        onDraftChange={(field, value) => setDraftField(key, field, value)}
                        onApply={() => applyStream(row.channel, streamType)}
                        onReset={() => resetStream(key)}
                        applyResult={applyResult}
                        invalidFields={invalidFields}
                        peerMissing={streamType === "sub" && gatherLoaded && !gatherRow?.main}
                        isConfiguredStream={!isIpCamera && configuredStreamType === streamType}
                      />
                    </Col>
                  );
                };
                return (
                  <div key={camera.public_id}>
                    <div className="d-flex align-items-center flex-wrap mb-1">
                      <h5 className="mb-0 mr-2">
                        Camera {camera.slot_number}
                        {viewNames(camera) ? ` — ${viewNames(camera)}` : ""}
                      </h5>
                      {cameraHasPolygon(camera) && (
                        <i className="fa fa-draw-polygon text-info mr-2" title="Has analysis polygon" />
                      )}
                      {!camera.is_active && <Badge color="secondary">inactive</Badge>}
                    </div>
                    {row ? (
                      <>
                        <ChannelStatusHeader row={row} />
                        <Row className="mb-4 align-items-stretch">
                          {renderCard("main")}
                          {renderCard("sub")}
                        </Row>
                      </>
                    ) : (
                      <Alert color="secondary" className="mb-4">
                        {slot == null
                          ? "This camera has no DVR channel number, so it cannot be configured here."
                          : `The device reported no configuration for channel ${slot}, so this camera cannot be configured right now. Retry "Get current config", or check the channel on the device.`}
                      </Alert>
                    )}
                  </div>
                );
              })}
          </div>
        ) : null}
        {rawOutput ? (
          <pre
            className="small mb-0 mt-2"
            style={{
              maxHeight: "60vh",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {rawOutput}
          </pre>
        ) : null}
      </CardBody>
    </Card>
  );
}

export default CameraModelDvrPanel;
